/**
 * Canvas-based signature input.
 *
 * Renders a fixed-size canvas the user signs with mouse or finger;
 * stroke events use Pointer Events (works for mouse + touch + pen
 * uniformly). On blur / unmount, ``onChange`` receives the canvas
 * as a base64-encoded data URL — that's the value the renderer
 * stores in ``form_data[fieldId]``.
 *
 * "Clear" button resets the canvas + fires ``onChange("")``.
 */
import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Eraser } from "lucide-react";

interface SignaturePadProps {
  /** Existing signature (data URL). When set, the canvas re-paints
   *  it on mount. */
  value?: string | null;
  onChange: (dataUrl: string) => void;
  width?: number;
  height?: number;
  disabled?: boolean;
}

export function SignaturePad({
  value,
  onChange,
  width = 480,
  height = 160,
  disabled = false,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(Boolean(value));

  // Paint the existing value on mount + size the canvas backing
  // store at devicePixelRatio so strokes stay crisp on hi-dpi
  // displays without making the on-screen size huge.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2;

    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        setHasInk(true);
      };
      img.src = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getPos = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = getPos(e);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const p = getPos(e);
    const last = lastPointRef.current;
    if (!last) return;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    setHasInk(true);
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    canvasRef.current?.releasePointerCapture(e.pointerId);
    // Push the value upstream on stroke end. Cheaper than firing
    // toDataURL on every pointermove.
    if (canvasRef.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange("");
  };

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-border/60 bg-white inline-block touch-none">
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          className={`block ${disabled ? "opacity-60" : "cursor-crosshair"}`}
        />
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={clear}
          disabled={disabled || !hasInk}
          className="text-xs text-text-muted hover:text-text-primary inline-flex items-center gap-1 disabled:opacity-40"
        >
          <Eraser className="h-3.5 w-3.5" />
          Clear
        </button>
        {!hasInk && (
          <span className="text-xs text-text-muted italic">
            Sign with mouse, trackpad, or finger
          </span>
        )}
      </div>
    </div>
  );
}

export default SignaturePad;
